import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { PatternFormat } from 'react-number-format';
import AnimatedComponent from '../Components/AnimatedComponent';
import Button from '../Components/Button';
import MobileHeroDiagonal from '../Components/MobileHeroDiagonal';
import NavigationBar from '../Components/NavigationBar';
import { LanguageContext } from '../Components/LanguageContext';
import handleTransformLang from '../functions/handleTransformLang';
import Layout from '../Layout';
import NavLang from '../lang/pages/nav.json';
import hrPage from '../lang/pages/hr.json';
import Background from '../public/images/bg_main.svg';

const INPUT_CLASS =
  'block w-full border border-[#aeb5c0] bg-white px-4 py-3 text-[17px] text-text_secondary transition focus:outline-none focus:ring-2 focus:ring-text_primary placeholder:text-[#aeb6c3]';
const SMALL_INPUT_CLASS =
  'block w-full border border-[#aeb5c0] bg-white px-4 py-3 text-[17px] text-text_secondary transition focus:outline-none focus:ring-2 focus:ring-text_primary';

function createEmptyForm() {
  return {
    fullName: '',
    phone: '',
    identityNo: '',
    address: '',
    email: '',
    city: '',
    district: '',
    experienceYear: '',
    experienceMonth: '',
    birthDay: '',
    birthMonth: '',
    birthYear: '',
    birthDate: '',
    experience: '',
    about: '',
  };
}

function pairItems(items = []) {
  const rows = [];

  for (let index = 0; index < items.length; index += 2) {
    rows.push(items.slice(index, index + 2));
  }

  return rows;
}

export default function HumanResources() {
  return (
    <Layout headData={hrPage.head}>
      <InnerPage />
    </Layout>
  );
}

function InnerPage() {
  const { language } = useContext(LanguageContext);
  const [content, setContent] = useState(handleTransformLang(NavLang));
  const [pageContent, setPageContent] = useState(handleTransformLang(hrPage));
  const [formData, setFormData] = useState(createEmptyForm);
  const [cvFileName, setCvFileName] = useState('');
  const applicationRef = useRef(null);
  const positionRows = useMemo(
    () => pairItems(pageContent.positions?.items || []),
    [pageContent.positions]
  );

  useEffect(() => {
    setContent(handleTransformLang(NavLang));
    setPageContent(handleTransformLang(hrPage));
  }, [language]);

  function updateField(name, value) {
    setFormData((current) => ({
      ...current,
      [name]: value,
    }));
  }

  function handleScrollToApplication() {
    applicationRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }

  function handleFileChange(event) {
    const file = event.target.files?.[0];
    setCvFileName(file?.name || '');
  }

  return (
    <>
      <section
        className="relative overflow-hidden bg-white pb-24 lg:min-h-[760px] lg:pb-28"
      >
        <div
          className="absolute inset-0 hidden bg-no-repeat sm:block sm:bg-[length:1180px_480px] sm:bg-top 1.5md:bg-[length:1560px_620px] 1.5md:bg-top xl:bg-[length:1760px_700px] xl:bg-top 1.5xl:bg-[length:1915px_750px] 1.5xl:bg-top"
          style={{ backgroundImage: `url(${Background})` }}
        />
        <MobileHeroDiagonal className="top-[11.25rem] h-[16.5rem] w-[12.6rem] sm:top-[5rem]" />
        <div className="h-1.5 w-full bg-bg_secondary" />

        <div className="relative z-20 container mx-auto max-w-8xl px-4 py-11">
          <NavigationBar
            content={content}
            desktopNavBreakpoint="1.5xl"
            logoClassName="h-18 sm:h-20 1.5md:h-22 1.5xl:h-25"
            b2bButtonClassName="px-4 py-[0.45rem] text-[13px] 1.5md:px-4 1.5md:py-[0.45rem] 1.5md:text-[14px] 1.5xl:px-5 1.5xl:py-2 1.5xl:text-lg"
            hamburgerIconClassName="h-9 w-9 sm:h-10 sm:w-10 1.5md:h-11 1.5md:w-11"
          />

          <div className="grid items-center gap-14 pt-10 lg:grid-cols-[0.84fr_1.16fr] lg:pt-16">
            <AnimatedComponent
              direction="animate-fade-right"
              className="order-2 max-w-[20rem] sm:max-w-[23rem] lg:order-1 lg:ml-[-15px] lg:max-w-[31rem] lg:pt-[216px]"
            >
              <h1 className="text-[23px] font-bold leading-[1.12] text-text_primary sm:text-[25px] 1.5md:text-[28px] xl:text-[32px] 1.5xl:text-[36px]">
                {pageContent.hero.title}
              </h1>

              <p className="mt-3 max-w-[20rem] text-[13px] leading-[1.55] text-text_secondary sm:max-w-[23rem] sm:text-[14px] 1.5md:mt-4 1.5md:max-w-[18rem] 1.5md:text-[13.5px] xl:max-w-[23rem] xl:text-[15px] xl:leading-7 1.5xl:mt-5 1.5xl:max-w-[29rem] 1.5xl:text-[17px] 1.5xl:leading-8">
                {pageContent.hero.description}
              </p>

              <div className="mt-3 space-y-1.5 sm:space-y-2 1.5md:mt-4 1.5xl:mt-4 1.5xl:space-y-2.5">
                {pageContent.hero.items.map((item) => (
                  <div
                    key={item}
                    className="flex items-start gap-2 text-[13px] leading-[1.45] text-text_secondary sm:text-[13.5px] xl:text-[14px] xl:leading-6 1.5xl:gap-2.5 1.5xl:text-[16px] 1.5xl:leading-7"
                  >
                    <img
                      src="/images/hr/check_mark.svg"
                      alt=""
                      className="mt-0.5 h-3.5 w-3.5 shrink-0 sm:mt-1 sm:h-4 sm:w-4"
                    />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </AnimatedComponent>

            <AnimatedComponent
              direction="animate-fade-left"
              className="relative order-1 lg:order-2 lg:ps-2"
            >
              <img
                src="/images/hr/banner_image.svg"
                alt=""
                className="relative z-10 mx-auto mt-[1.875rem] w-full max-w-[23rem] object-contain sm:mt-[3.125rem] lg:mt-0 lg:max-w-[34.5rem] lg:translate-x-[9px] lg:translate-y-[134px]"
              />
            </AnimatedComponent>
          </div>
        </div>
      </section>

      <section id="positions" className="bg-white pb-20 pt-8 lg:pb-28 lg:pt-12">
        <div className="container mx-auto max-w-8xl px-4">
          <AnimatedComponent direction="animate-fade-up animate-duration-500">
            <h2 className="text-[32px] font-bold leading-tight text-text_primary">
              {pageContent.positions.title}
            </h2>
          </AnimatedComponent>

          <div className="mt-8 sm:mt-10">
            {positionRows.map((row, rowIndex) => (
              <div
                key={`row-${rowIndex}`}
                className={`grid border-[#b7beca] lg:grid-cols-2 ${
                  rowIndex === 0 ? 'border-t' : ''
                }`}
              >
                {row.map((item, itemIndex) => (
                  <AnimatedComponent
                    key={`${item}-${itemIndex}`}
                    direction="animate-fade-up animate-duration-500"
                    className={`flex items-center justify-between gap-5 border-b border-[#b7beca] py-5 sm:py-6 xl:py-8 ${
                      itemIndex === 1 ? 'lg:border-s lg:ps-10 xl:ps-16' : 'lg:pe-10 xl:pe-16'
                    }`}
                  >
                    <span className="text-[17px] font-semibold leading-snug text-text_primary sm:text-[18px] xl:text-[23px]">
                      {item}
                    </span>

                    <Button
                      className="min-w-[13rem] px-0 max-[700px]:min-w-[10.75rem] max-[700px]:px-6 max-[700px]:py-1 max-[700px]:text-[15px] sm:min-w-[11rem] xl:min-w-[10.4rem]"
                      onClick={handleScrollToApplication}
                    >
                      {pageContent.positions.buttonLabel}
                    </Button>
                  </AnimatedComponent>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        id="application"
        ref={applicationRef}
        className="bg-white pb-28 pt-8 lg:pb-32 lg:pt-14"
      >
        <div className="container mx-auto max-w-8xl px-4">
          <AnimatedComponent
            direction="animate-fade-up animate-duration-500"
            className="max-w-[88rem]"
          >
            <h2 className="text-[32px] font-bold leading-tight text-text_primary">
              {pageContent.application.title}
            </h2>

            <p className="mt-5 hidden max-w-[74rem] text-[17px] leading-8 text-text_secondary lg:block">
              {pageContent.application.description}
            </p>

            <form
              className="mt-10 hidden lg:block lg:mt-14"
              onSubmit={(event) => event.preventDefault()}
            >
              <div className="grid gap-x-24 gap-y-10 lg:grid-cols-2">
                <HrField label={pageContent.application.fields.fullName}>
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(event) => updateField('fullName', event.target.value)}
                    className={INPUT_CLASS}
                  />
                </HrField>

                <HrField label={pageContent.application.fields.phone}>
                  <PatternFormat
                    format="0 (###) ### ## ##"
                    mask="_"
                    value={formData.phone}
                    onValueChange={(values) => updateField('phone', values.value)}
                    className={INPUT_CLASS}
                  />
                </HrField>

                <HrField label={pageContent.application.fields.identityNo}>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={11}
                    value={formData.identityNo}
                    onChange={(event) => updateField('identityNo', event.target.value)}
                    className={INPUT_CLASS}
                  />
                </HrField>

                <HrField label={pageContent.application.fields.address}>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(event) => updateField('address', event.target.value)}
                    className={INPUT_CLASS}
                  />
                </HrField>

                <HrField label={pageContent.application.fields.email}>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(event) => updateField('email', event.target.value)}
                    className={INPUT_CLASS}
                  />
                </HrField>

                <HrField label={pageContent.application.fields.cityDistrict}>
                  <div className="flex items-center gap-4">
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(event) => updateField('city', event.target.value)}
                      className={INPUT_CLASS}
                    />
                    <span className="text-[24px] leading-none text-text_secondary">/</span>
                    <input
                      type="text"
                      value={formData.district}
                      onChange={(event) => updateField('district', event.target.value)}
                      className={INPUT_CLASS}
                    />
                  </div>
                </HrField>

                <HrField label={pageContent.application.fields.experience}>
                  <div className="flex flex-wrap items-center gap-4">
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={2}
                      value={formData.experienceYear}
                      onChange={(event) => updateField('experienceYear', event.target.value)}
                      className={`${SMALL_INPUT_CLASS} max-w-[6.4rem]`}
                    />
                    <span className="text-[17px] font-semibold text-text_secondary">
                      {pageContent.application.fields.experienceYear}
                    </span>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={2}
                      value={formData.experienceMonth}
                      onChange={(event) => updateField('experienceMonth', event.target.value)}
                      className={`${SMALL_INPUT_CLASS} max-w-[6.4rem]`}
                    />
                    <span className="text-[17px] font-semibold text-text_secondary">
                      {pageContent.application.fields.experienceMonth}
                    </span>
                  </div>
                </HrField>

                <HrField label={pageContent.application.fields.birthDate}>
                  <div className="flex flex-wrap items-center gap-4">
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={2}
                      value={formData.birthDay}
                      onChange={(event) => updateField('birthDay', event.target.value)}
                      className={`${SMALL_INPUT_CLASS} max-w-[6rem]`}
                    />
                    <span className="text-[24px] leading-none text-text_secondary">/</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={2}
                      value={formData.birthMonth}
                      onChange={(event) => updateField('birthMonth', event.target.value)}
                      className={`${SMALL_INPUT_CLASS} max-w-[6rem]`}
                    />
                    <span className="text-[24px] leading-none text-text_secondary">/</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={4}
                      value={formData.birthYear}
                      onChange={(event) => updateField('birthYear', event.target.value)}
                      className={`${SMALL_INPUT_CLASS} max-w-[10rem]`}
                    />
                  </div>
                </HrField>
              </div>

              <div className="mt-14">
                <label className="block text-[17px] font-semibold text-text_secondary">
                  {pageContent.application.fields.about}
                </label>

                <textarea
                  value={formData.about}
                  onChange={(event) => updateField('about', event.target.value)}
                  rows={9}
                  className={`${INPUT_CLASS} mt-4 min-h-[18rem] resize-none px-5 py-5`}
                />
              </div>

              <div className="mt-14">
                <p className="text-[17px] font-semibold text-text_secondary">
                  {pageContent.application.fields.cv}
                </p>

                <div className="mt-5 flex flex-col gap-4 xl:flex-row xl:items-center">
                  <label className="relative flex h-[3.2rem] w-full max-w-[29rem] cursor-pointer items-center overflow-hidden border border-[#aeb5c0] bg-white pe-[9rem] ps-5 text-[17px] text-text_secondary">
                    <span className={cvFileName ? 'text-text_primary' : 'text-[#aeb6c3]'}>
                      {cvFileName || pageContent.application.fields.uploadPlaceholder}
                    </span>

                    <span className="absolute inset-y-0 right-0 flex w-[8.65rem] items-center justify-center gap-2">
                      <img
                        src="/images/hr/gozat_zemin.svg"
                        alt=""
                        className="absolute inset-0 h-full w-full"
                      />
                      <img
                        src="/images/hr/gozat_zemin_1.svg"
                        alt=""
                        className="relative h-7 w-auto"
                      />
                      <span className="relative z-10 text-[17px] font-semibold text-white">
                        {pageContent.application.fields.uploadButtonLabel}
                      </span>
                    </span>

                    <input
                      type="file"
                      className="sr-only"
                      onChange={handleFileChange}
                    />
                  </label>

                  <p className="text-[17px] leading-8 text-text_secondary">
                    {pageContent.application.fields.uploadNote}
                  </p>
                </div>
              </div>

              <div className="mt-16 flex justify-end">
                <Button
                  type="submit"
                  className="min-w-[10.4rem]"
                >
                  {pageContent.application.submit}
                </Button>
              </div>
            </form>

            <form
              className="mt-8 lg:hidden"
              onSubmit={(event) => event.preventDefault()}
            >
              <div className="space-y-6">
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(event) => updateField('fullName', event.target.value)}
                  className={INPUT_CLASS}
                  placeholder={pageContent.application.fields.fullName}
                />

                <PatternFormat
                  format="0 (###) ### ## ##"
                  mask="_"
                  value={formData.phone}
                  onValueChange={(values) => updateField('phone', values.value)}
                  className={INPUT_CLASS}
                  placeholder={pageContent.application.fields.phone}
                />

                <input
                  type="email"
                  value={formData.email}
                  onChange={(event) => updateField('email', event.target.value)}
                  className={INPUT_CLASS}
                  placeholder={pageContent.application.fields.email}
                />

                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={11}
                  value={formData.identityNo}
                  onChange={(event) => updateField('identityNo', event.target.value)}
                  className={INPUT_CLASS}
                  placeholder={pageContent.application.fields.identityNo}
                />

                <input
                  type="text"
                  value={formData.address}
                  onChange={(event) => updateField('address', event.target.value)}
                  className={INPUT_CLASS}
                  placeholder={pageContent.application.fields.address}
                />

                <div className="grid grid-cols-2 gap-5">
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(event) => updateField('city', event.target.value)}
                    className={INPUT_CLASS}
                    placeholder={pageContent.application.fields.city}
                  />
                  <input
                    type="text"
                    value={formData.district}
                    onChange={(event) => updateField('district', event.target.value)}
                    className={INPUT_CLASS}
                    placeholder={pageContent.application.fields.district}
                  />
                </div>

                <input
                  type="text"
                  value={formData.birthDate}
                  onChange={(event) => updateField('birthDate', event.target.value)}
                  className={INPUT_CLASS}
                  placeholder={pageContent.application.fields.birthDate}
                />

                <input
                  type="text"
                  value={formData.experience}
                  onChange={(event) => updateField('experience', event.target.value)}
                  className={INPUT_CLASS}
                  placeholder={pageContent.application.fields.experience}
                />

                <textarea
                  value={formData.about}
                  onChange={(event) => updateField('about', event.target.value)}
                  rows={7}
                  className={`${INPUT_CLASS} min-h-[15rem] resize-none px-4 py-4`}
                  placeholder={pageContent.application.fields.about}
                />
              </div>

              <div className="mt-12">
                <p className="text-[17px] font-semibold text-text_secondary">
                  {pageContent.application.fields.cv}
                </p>
                <p className="mt-2 max-w-[22rem] text-[17px] leading-8 text-text_secondary">
                  {pageContent.application.fields.uploadNote}
                </p>

                <div className="mt-8 flex items-center justify-between gap-6 max-[430px]:gap-3">
                  <label className="inline-flex h-fit min-w-[14rem] cursor-pointer items-center justify-center rounded-full bg-[#7c8390] px-8 py-3 text-[17px] font-bold text-white transition-colors duration-300 hover:bg-[#6f7684] max-[700px]:min-w-[11.5rem] max-[700px]:px-6 max-[700px]:py-2.5 max-[700px]:text-[15px] max-[430px]:min-w-0 max-[430px]:px-7 max-[430px]:text-[15px]">
                    {pageContent.application.fields.mobileUploadButtonLabel}
                    <input
                      type="file"
                      className="sr-only"
                      onChange={handleFileChange}
                    />
                  </label>

                  <Button type="submit" className="min-w-[14rem] px-0 max-[700px]:min-w-[11.5rem] max-[700px]:px-6 max-[700px]:py-2.5 max-[700px]:text-[15px] max-[430px]:min-w-0 max-[430px]:px-7 max-[430px]:text-[15px]">
                    {pageContent.application.submit}
                  </Button>
                </div>

                {cvFileName ? (
                  <p className="mt-4 text-[15px] text-text_secondary">
                    {cvFileName}
                  </p>
                ) : null}
              </div>
            </form>
          </AnimatedComponent>
        </div>
      </section>
    </>
  );
}

function HrField({ label, children }) {
  return (
    <div className="grid gap-3 xl:grid-cols-[150px_1fr] xl:items-center">
      <label className="text-left text-[17px] font-semibold text-text_secondary xl:text-right">
        {label}
      </label>
      <div>{children}</div>
    </div>
  );
}
